<template>
  <form @submit.prevent="handleSubmit" class="space-y-4">
    <!-- 标题 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        标题 <span class="text-red-500">*</span>
      </label>
      <input
        v-model="formData.title"
        type="text"
        required
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
        placeholder="请输入书籍标题"
      />
    </div>

    <!-- 描述 -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
      <textarea
        v-model="formData.description"
        rows="3"
        class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
        placeholder="请输入书籍描述"
      ></textarea>
    </div>

    <!-- 分类和子分类 -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">主分类</label>
        <select
          v-model="formData.category_id"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
          @change="handleCategoryChange"
        >
          <option :value="null">未分类</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">
            {{ category.name }}
          </option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">子分类</label>
        <select
          v-model="formData.subcategory_id"
          :disabled="!formData.category_id || !subcategories.length"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 disabled:bg-gray-100 dark:disabled:bg-gray-600"
        >
          <option :value="null">未分类</option>
          <option v-for="subcategory in subcategories" :key="subcategory.id" :value="subcategory.id">
            {{ subcategory.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- 语言和难度 -->
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          语言 <span class="text-red-500">*</span>
        </label>
        <select
          v-model="formData.language_id"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
        >
          <option :value="1">英语</option>
          <option :value="2">中文</option>
          <option :value="3">日语</option>
          <option :value="4">韩语</option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          难度等级 <span class="text-red-500">*</span>
        </label>
        <select
          v-model="formData.difficulty_level"
          required
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200"
        >
          <option :value="1">★☆☆☆☆ (入门)</option>
          <option :value="2">★★☆☆☆ (初级)</option>
          <option :value="3">★★★☆☆ (中级)</option>
          <option :value="4">★★★★☆ (高级)</option>
          <option :value="5">★★★★★ (专家)</option>
        </select>
      </div>
    </div>

    <!-- 状态 -->
    <div class="flex gap-6">
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          v-model="formData.is_public"
          type="checkbox"
          class="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500"
        />
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">公开（用户可见）</span>
      </label>
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          v-model="formData.is_free"
          type="checkbox"
          class="w-4 h-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500"
        />
        <span class="text-sm font-medium text-gray-700 dark:text-gray-300">免费（无需付费）</span>
      </label>
    </div>

    <!-- 提交按钮 -->
    <div class="flex justify-end gap-3 pt-4">
      <button
        type="button"
        class="cancel-btn text-sm font-medium"
        @click="$emit('cancel')"
      >
        取消
      </button>
      <button
        type="submit"
        :disabled="loading"
        class="primary-btn text-sm font-medium disabled:opacity-50"
      >
        <span v-if="loading" class="inline-block w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
        {{ loading ? '保存中...' : '保存' }}
      </button>
    </div>
  </form>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import type { BookSchema } from '@/types/dictation/book';
import type { CategorySchema } from '@/types/dictation/category';

/**
 * 书籍表单组件
 */

interface Props {
  /** 初始数据（编辑模式） */
  initialData?: Partial<BookSchema>;
  /** 分类列表 */
  categories?: CategorySchema[];
  /** 加载状态 */
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  categories: () => [],
  loading: false,
});

const emit = defineEmits<{
  'submit': [data: any];
  'cancel': [];
}>();

// 表单数据
const formData = ref({
  title: '',
  description: '',
  category_id: null as number | null,
  subcategory_id: null as number | null,
  language_id: 1,
  difficulty_level: 3,
  is_public: true,
  is_free: false,
});

// 子分类列表
const subcategories = computed(() => {
  if (!formData.value.category_id) return [];
  const category = props.categories.find(c => c.id === formData.value.category_id);
  return category?.subcategories || [];
});

// 初始化表单数据
if (props.initialData) {
  Object.assign(formData.value, props.initialData);
}

// 监听初始数据变化
watch(() => props.initialData, (newData) => {
  if (newData) {
    Object.assign(formData.value, newData);
  }
}, { deep: true });

// 分类变化时清空子分类
const handleCategoryChange = () => {
  formData.value.subcategory_id = null;
};

// 提交表单
const handleSubmit = () => {
  emit('submit', { ...formData.value });
};
</script>

